import {
  Box,
  Button,
  ButtonProps,
  Stack,
  SvgIcon,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { LineAnimation } from './line-animation';
import { spinnerIcon } from '../config/icons';

export type ContainedButtonProps = Omit<ButtonProps, 'component' | 'color'> & {
  label: string;
  labelDirection?: 'ltr' | 'rtl';
  loading?: boolean;
  showLineAnimation?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | string;
  buttonSx?: SxProps;
};

export function ContainedButton({
  showLineAnimation,
  loading,
  label,
  labelDirection = undefined,
  icon,
  color = 'primary',
  onClick,
  buttonSx = {},
  ...props
}: ContainedButtonProps) {
  const { palette } = useTheme();
  const buttonColor = ({ palette }: any) =>
    color === 'primary' ? palette.primary.main : palette.success.main;
  const buttonHoverColor = ({ palette }: any) =>
    color === 'primary' ? palette.primary.light : palette.success.light;

  // LineAnimation is now imported from the component file

  return (
    <Button
      type={onClick ? 'button' : 'submit'}
      onClick={onClick}
      variant="contained"
      data-treedistestid="button-contained-action"
      sx={{
        borderRadius: '12px',
        background: buttonColor,
        position: 'relative',
        overflow: 'hidden',
        height: '44px',
        color: palette.common.white,
        pointerEvents: loading ? 'none' : 'auto',
        '&:hover': {
          background: buttonHoverColor,
          color: palette.common.white,
        },
        '&:active, &:focus': {
          background: buttonColor,
        },
        ...buttonSx,
      }}
      {...props}
    >
      <Box
        sx={{
          backgroundImage:
            'linear-gradient(270deg, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%);',
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      />
      {showLineAnimation && <LineAnimation to={250} />}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} width="100%">
        {loading && (
          <Stack width="30px" alignItems="center">
            {loading && <SvgIcon sx={{ width: '20px' }}>{spinnerIcon}</SvgIcon>}
          </Stack>
        )}
        {icon}

        <Typography
          whiteSpace="nowrap"
          dir={labelDirection}
          sx={{
            fontSize: '1rem !important',
            fontWeight: '600 !important',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: props.disabled && !loading ? palette.primary.main : palette.common.white,
          }}
        >
          {label}
        </Typography>
      </Stack>
    </Button>
  );
}
